class HeaderComponent extends HTMLElement {
  connectedCallback() {
    this.toggleMenu = this.toggleMenu.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKey = this.handleEscKey.bind(this);
    this.handleMenuLinkClick = this.handleMenuLinkClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.burgerMenu = this.querySelector('.header-nav__menu');
    this.menuPanel = this.querySelector('.menu-panel');
    this.header = this.querySelector('.site-header');
    
    // Find the Shopify section group wrapper
    this.headerGroup = this.closest('.shopify-section-group-header-group');

    // Get custom top spacing from data attribute
    this.customTopSpacing = this.dataset.customTopSpacing 
      ? parseInt(this.dataset.customTopSpacing, 10) 
      : 0;

    // Home page logo and icon color switching
    this.isHomePage = this.dataset.isHomePage === 'true';
    this.homeIconColor = this.dataset.homeIconColor || null;
    this.homeScrolledIconColor = this.dataset.homeScrolledIconColor || null;
    this.homeButtonTextColor = this.dataset.homeButtonTextColor || null;
    this.homeButtonBorderColor = this.dataset.homeButtonBorderColor || null;
    this.homeScrolledButtonTextColor = this.dataset.homeScrolledButtonTextColor || null;
    this.homeScrolledButtonBorderColor = this.dataset.homeScrolledButtonBorderColor || null;
    
    if (this.isHomePage) {
      // Find logo elements
      this.homeLogo = this.querySelector('.header-nav__logo-img--home');
      this.scrolledLogo = this.querySelector('.header-nav__logo-img--scrolled');
      this.menuIcon = this.burgerMenu;
      this.cartIcon = this.querySelector('.header-nav__cart-link .tap-area');
      this.headerButton = this.querySelector('.header-custom-nav__button');
    }

    // Scroll tracking variables
    this.lastScrollY = window.scrollY;
    this.scrollThreshold = 100;
    this.hideThreshold = 50; // Minimum scroll distance before hiding
    this.ticking = false;

    if (this.burgerMenu) {
      this.burgerMenu.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMenu();
      });
    }

    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscKey);

    const menuLinks = this.querySelectorAll('.menu-panel__link, .menu-panel__button');
    menuLinks.forEach((link) => {
      link.addEventListener('click', this.handleMenuLinkClick);
    });

    // Initialize scroll handler
    if (this.header) {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      // Set initial state
      this.updateHeaderState();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKey);
    window.removeEventListener('scroll', this.handleScroll);

    const menuLinks = this.querySelectorAll('.menu-panel__link, .menu-panel__button');
    menuLinks.forEach((link) => {
      link.removeEventListener('click', this.handleMenuLinkClick);
    });
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateHeaderState();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateHeaderState() {
    if (!this.header || !this.headerGroup) return;

    const currentScrollY = window.scrollY;
    const scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';
    const isScrolledPastThreshold = currentScrollY > this.scrollThreshold;
    const isScrolledPastHideThreshold = currentScrollY > this.hideThreshold;
    const isAtTop = currentScrollY <= 0;

    // Don't hide header if menu is open
    const isMenuOpen = this.menuPanel && this.menuPanel.classList.contains('active');

    // Determine header visibility first
    let isHeaderVisible = true;
    
    if (isAtTop) {
      // At top of page: always visible, transparent background
      this.header.classList.remove('hidden', 'scrolled');
      isHeaderVisible = true;
    } else if (isMenuOpen) {
      // Menu is open: keep header visible but apply scrolled state if needed
      this.header.classList.remove('hidden');
      isHeaderVisible = true;
      if (isScrolledPastThreshold) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }
    } else {
      // Handle scroll direction
      if (scrollDirection === 'down' && isScrolledPastHideThreshold) {
        // Scrolling down: hide header only after minimum scroll distance
        this.header.classList.add('hidden');
        isHeaderVisible = false;
        // Remove scrolled class when hiding
        this.header.classList.remove('scrolled');
      } else {
        // Scrolling up: show header immediately
        this.header.classList.remove('hidden');
        isHeaderVisible = true;
        // Add scrolled class if past threshold
        if (isScrolledPastThreshold) {
          this.header.classList.add('scrolled');
        } else {
          this.header.classList.remove('scrolled');
        }
      }
    }

    // Determine top spacing value based on state
    let topSpacing = 0;
    if (this.customTopSpacing > 0) {
      if (isAtTop) {
        // At top of page: top spacing is 0
        topSpacing = 0;
      } else if (isHeaderVisible) {
        // Header is visible and not at top: use custom spacing
        // This applies when scrolling up (header becomes visible)
        topSpacing = this.customTopSpacing;
      } else {
        // Header is hidden: top spacing is 0
        topSpacing = 0;
      }
    }

    // Apply top spacing to header group
    this.headerGroup.style.top = `${topSpacing}px`;

    // Update home page logo and icon colors if on home page
    if (this.isHomePage) {
      this.updateHomePageLogoAndIcons(isAtTop, this.header.classList.contains('scrolled'));
    }

    this.lastScrollY = currentScrollY;
  }

  updateHomePageLogoAndIcons(isAtTop, isScrolled) {
    // Determine if we should show scrolled state
    // Show scrolled logo/colors when header has scrolled class (white background)
    const shouldShowScrolled = isScrolled && !isAtTop;

    // Update logo visibility
    if (this.homeLogo && this.scrolledLogo) {
      if (shouldShowScrolled) {
        // Show scrolled logo, hide home logo
        this.homeLogo.style.opacity = '0';
        this.homeLogo.style.visibility = 'hidden';
        this.scrolledLogo.style.opacity = '1';
        this.scrolledLogo.style.visibility = 'visible';
      } else {
        // Show home logo, hide scrolled logo
        this.homeLogo.style.opacity = '1';
        this.homeLogo.style.visibility = 'visible';
        this.scrolledLogo.style.opacity = '0';
        this.scrolledLogo.style.visibility = 'hidden';
      }
    }

    // Update icon colors
    const iconColor = shouldShowScrolled 
      ? (this.homeScrolledIconColor || this.homeIconColor)
      : this.homeIconColor;

    if (iconColor) {
      // Apply color to menu icon bars
      if (this.menuIcon) {
        const menuBars = this.menuIcon.querySelectorAll('div');
        menuBars.forEach(bar => {
          bar.style.backgroundColor = iconColor;
        });
      }

      // Apply color to cart icon via CSS custom properties (matching product page approach)
      // The CSS will handle the color switching via CSS variables
      // No need to set inline styles as CSS handles it via data attributes and classes
    }

    // Update button colors (text and border only, hover stays the same)
    if (this.headerButton) {
      const buttonTextColor = shouldShowScrolled
        ? (this.homeScrolledButtonTextColor || this.homeButtonTextColor)
        : this.homeButtonTextColor;
      
      const buttonBorderColor = shouldShowScrolled
        ? (this.homeScrolledButtonBorderColor || this.homeButtonBorderColor)
        : this.homeButtonBorderColor;

      if (buttonTextColor) {
        this.headerButton.style.color = buttonTextColor;
      }

      if (buttonBorderColor) {
        this.headerButton.style.borderColor = buttonBorderColor;
      }
    }
  }

  toggleMenu() {
    const isCurrentlyActive = this.menuPanel.classList.contains('active');
    const isMobile = window.innerWidth <= 767;

    if (!isCurrentlyActive) {
      this.burgerMenu.classList.add('active');
      this.menuPanel.classList.add('active');
      this.overlay = document.createElement('div');
      this.overlay.className = 'menu-overlay';
      this.overlay.addEventListener('click', this.handleOutsideClick);
      document.body.appendChild(this.overlay);

      if (isMobile) {
        document.body.classList.add('menu-open');
      }
    } else {
      this.burgerMenu.classList.remove('active');

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.handleOutsideClick);
        document.body.removeChild(this.overlay);
        this.overlay = null;
      }

      if (isMobile) {
        document.body.classList.remove('menu-open');
        setTimeout(() => {
          this.menuPanel.classList.remove('active');
        }, 10);
      } else {
        this.menuPanel.classList.remove('active');
      }
    }
  }

  handleOutsideClick(e) {
    if (
      this.menuPanel.classList.contains('active') &&
      !this.menuPanel.contains(e.target) &&
      e.target !== this.burgerMenu &&
      !this.burgerMenu.contains(e.target)
    ) {
      this.toggleMenu();
    }
  }

  handleEscKey(e) {
    if (e.key === 'Escape' && this.menuPanel.classList.contains('active')) {
      this.toggleMenu();
    }
  }

  handleMenuLinkClick() {
    this.toggleMenu();
  }
}

customElements.define('header-component', HeaderComponent);
